import React, { useCallback, useMemo } from 'react'
import {
    MediaWithCategories,
    StoredMediaCategory,
} from '../../../../types/Media'
import cx from 'classnames'
import styles from './CreateMediaModal.module.scss'
import CategoryMediaSelect from '../CategoryMediaSelect/CategoryMediaSelect'
import Input from '../../../../components/Input'
import Button from '../../../../components/Button'
import Heading from '../../../../components/Heading'
import DropInput from '../../../../components/DropInput'
import { useTranslation } from 'react-i18next'
import { useStoreData } from '../../../../hooks/useStoreData'
import * as yup from 'yup'
import { getRecParents } from '../../../../helpers/getRecParents'
import { ApiRoutes } from '../../../../services/ApiService'
import { closeModal } from '../../../../services/ModalService'
import { showToast } from '../../../../services/ToastService'

const CreateMediaModal: React.FC<{ onSubmit: () => void }> = ({ onSubmit }) => {
    const { t } = useTranslation(['settings'])
    const storeMediaSchema = useMemo(() => {
        return {
            name: yup.string().required().min(3),
            media_category_ids: yup.array().required().min(1),
        }
    }, [])

    const {
        element,
        changeProp,
        isValid,
        changeFile,
        submit,
        clearData,
        files,
    } = useStoreData<MediaWithCategories>(ApiRoutes.MEDIA, storeMediaSchema)

    const toggleCategory = useCallback(
        (value: number, tree: StoredMediaCategory[]) => {
            if (element.media_category_ids?.includes(value)) {
                const unique = [
                    ...new Set(
                        element.media_category_ids.filter((id) => id !== value)
                    ),
                ]
                changeProp({ media_category_ids: unique })
            } else {
                const unique = [
                    ...new Set([
                        ...(element.media_category_ids ?? []),
                        ...getRecParents(value, tree),
                    ]),
                ]
                changeProp({ media_category_ids: unique })
            }
        },
        [changeProp, element.media_category_ids]
    )

    const handleChangeName = (value: string) => {
        changeProp({ name: value })
    }

    const handleFileChange = (selectedFile: File) => {
        changeFile(selectedFile)
    }

    const handleSubmit = async () => {
        try {
            const res = await submit()
            if (res.status === 201) {
                clearData()
                closeModal()
                showToast(t('media.create_modal.success'))
                onSubmit()
            } else {
                showToast(t('media.create_modal.error'), 'error')
                onSubmit()
            }
        } catch (err: any) {
            // Handle 422 validation errors from backend
            if (err.response?.status === 422) {
                const errorMessage = err.response?.data?.message || t('media.create_modal.error')
                showToast(errorMessage, 'error')
            } else {
                showToast(t('media.create_modal.error'), 'error')
            }
            onSubmit()
        }
    }

    return (
        <div className={cx(styles['zr-modal-wrapper'])}>
            <div className={cx(styles['zr-modal-content-wrapper'])}>
                <Heading level={4}>
                    {t('media.create_modal.sub_heading')}
                </Heading>
                <Input
                    type={'text'}
                    placeholder={t('media.labels.title')}
                    property={'title'}
                    value={element.name ?? ''}
                    onChange={(_, value) => handleChangeName(value)}
                />
                <div className={cx(styles['upload-label'])}>
                    {t('media.labels.file')}
                    <DropInput
                        onFileChange={handleFileChange}
                        accept={'image/*'}
                        previewSrc={
                            files.length > 0
                                ? URL.createObjectURL(files[0])
                                : undefined
                        }
                    />
                </div>
                <Button
                    hierarchy={'primary'}
                    text={t('media.action')}
                    fn={() => handleSubmit()}
                    classes={'zr-w-100'}
                    disabled={!isValid || files.length === 0}
                />
            </div>
            <CategoryMediaSelect
                selectedCategoryIds={element.media_category_ids ?? []}
                onToggle={toggleCategory}
                enableAddNewCategory={true}
            />
        </div>
    )
}

export default CreateMediaModal
